// ============================================================================
// SiFa Flow: Sentry PII-Scrubber
// ----------------------------------------------------------------------------
// Redacts PII shapes (emails, phones, IBANs, UUIDs, JWTs, Bearer tokens,
// Authorization/Cookie headers, request bodies, query strings) from Sentry
// events + breadcrumbs.
//
// DSGVO-Hard-Rule (siehe AGENTS.md):
//   - Niemals raw PII zu Sentry schicken
//   - Bei neuen PII-Feld-Patterns: hier ergänzen + Tests hinzufügen
// ============================================================================

import type { ErrorEvent, Breadcrumb, EventHint } from "@sentry/core";

type Event = ErrorEvent;

const REDACTED = "[redacted]";

// PII-Patterns
// Reihenfolge: Spezifische Shapes ZUERST (Gemini/Supabase/Stripe), dann
// generische API-Key-Pattern, dann PII (Email/Phone/IBAN/UUID/JWT/Bearer).
const PATTERNS: Array<{ name: string; rx: RegExp; replace: string }> = [
  // Gemini / Google API-Key: AIza... typischerweise 39 chars total (4 prefix + 35 body),
  // service-account-tokens auch länger. Greedy ab 35 body-chars, kein Boundary
  // (Boundary scheitert bei längeren Tokens, weil [A-Za-z0-9_-] keine word-Grenzen kennt).
  { name: "gemini_api_key", rx: /AIza[A-Za-z0-9_-]{35,}/g, replace: "[redacted-google-key]" },
  // Stripe (legacy / fallout): sk_test_... / pk_live_... / rk_test_...
  { name: "stripe_token", rx: /\b(?:sk|pk|rk)_(?:test|live)_[A-Za-z0-9]{16,}\b/g, replace: "[redacted-stripe-token]" },
  // Supabase access/refresh-token-Cookies: sb-projref-access-token=eyJ...
  { name: "supabase_session_cookie", rx: /sb-[A-Za-z0-9-]+-(?:access|refresh)-token=[A-Za-z0-9._-]+/gi, replace: "sb-[redacted]-token" },
  // Supabase publishable / secret key: sb_publishable_xxx, sb_secret_xxx
  { name: "supabase_key", rx: /\bsb_[a-z]+_[A-Za-z0-9_-]{20,}\b/g, replace: "[redacted-supabase-key]" },
  // Generischer API-Key: api_key=..., x-api-key:..., apikey:...
  { name: "generic_api_key", rx: /\b(?:api[_-]?key|x-api-key|apikey)\s*[:=]\s*["']?[A-Za-z0-9_-]{16,}["']?/gi, replace: "[redacted-api-key]" },
  // Email: foo@bar.com → [redacted-email]
  { name: "email", rx: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, replace: "[redacted-email]" },
  // IBAN: DE89370400440532013000 → [redacted-iban]
  { name: "iban", rx: /\b[A-Z]{2}\d{2}[A-Z0-9]{4}\d{7}([A-Z0-9]?){0,16}\b/g, replace: "[redacted-iban]" },
  // JWT: eyJ...
  { name: "jwt", rx: /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, replace: "[redacted-jwt]" },
  // Bearer Token: Bearer abc...
  { name: "bearer", rx: /Bearer\s+[A-Za-z0-9._\-+/=]+/gi, replace: "Bearer [redacted]" },
  // German Phone: +49 / 0049 / 0... mit 7-15 Ziffern
  { name: "phone", rx: /(\+49|0049|0)[\s\-/()]?\d{2,5}[\s\-/()]?\d{4,12}/g, replace: "[redacted-phone]" },
  // UUID: 123e4567-e89b-12d3-a456-426614174000
  { name: "uuid", rx: /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, replace: "[redacted-uuid]" },
];

// Header-Keys, die vollständig redacted werden
const SENSITIVE_HEADERS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-supabase-auth",
  "sb-access-token",
  "sb-refresh-token",
]);

export function scrubString(input: string | undefined | null): string | null | undefined {
  if (!input) return input;
  let out = input;
  for (const { rx, replace } of PATTERNS) {
    out = out.replace(rx, replace);
  }
  return out;
}

export function scrubObject<T>(obj: T, depth = 0): T {
  if (depth > 10) return obj; // Tiefenlimit gegen zirkuläre Refs
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === "string") return scrubString(obj) as unknown as T;
  if (Array.isArray(obj)) {
    return obj.map((v) => scrubObject(v, depth + 1)) as unknown as T;
  }
  if (typeof obj !== "object") return obj;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    const lower = key.toLowerCase();
    if (SENSITIVE_HEADERS.has(lower)) {
      result[key] = REDACTED;
      continue;
    }
    // Bekannte PII-Feld-Namen
    if (
      lower === "email" ||
      lower === "phone" ||
      lower === "iban" ||
      lower === "bic" ||
      lower === "password" ||
      lower === "pwd" ||
      lower === "token" ||
      lower === "secret" ||
      lower === "ssn"
    ) {
      result[key] = REDACTED;
      continue;
    }
    result[key] = scrubObject(value, depth + 1);
  }
  return result as T;
}

export function scrubEvent(event: Event, _hint?: EventHint): Event | null {
  // Request-Daten: Body, Querystring, Headers, Cookies
  if (event.request) {
    if (event.request.headers) {
      const cleanHeaders: Record<string, string> = {};
      for (const [k, v] of Object.entries(event.request.headers)) {
        cleanHeaders[k] = SENSITIVE_HEADERS.has(k.toLowerCase())
          ? REDACTED
          : (scrubString(String(v)) ?? "");
      }
      event.request.headers = cleanHeaders;
    }
    if (event.request.cookies) {
      const cleanCookies: Record<string, string> = {};
      for (const k of Object.keys(event.request.cookies)) {
        cleanCookies[k] = REDACTED;
      }
      event.request.cookies = cleanCookies;
    }
    if (event.request.data) event.request.data = scrubObject(event.request.data);
    if (event.request.query_string) {
      event.request.query_string =
        typeof event.request.query_string === "string"
          ? scrubString(event.request.query_string) ?? ""
          : event.request.query_string;
    }
    if (event.request.url) {
      event.request.url = scrubString(event.request.url) ?? event.request.url;
    }
  }

  // User: Email/IP raus
  if (event.user) {
    delete event.user.email;
    delete event.user.ip_address;
    delete event.user.username;
  }

  // Message + Exception-Stack: scrubben
  if (event.message) {
    event.message = scrubString(event.message) ?? event.message;
  }
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = scrubString(ex.value) ?? ex.value;
    }
  }

  // Extra/Tags/Contexts
  if (event.extra) event.extra = scrubObject(event.extra);
  if (event.contexts) event.contexts = scrubObject(event.contexts);

  return event;
}

export function scrubBreadcrumb(
  breadcrumb: Breadcrumb,
  _hint?: { event?: Event },
): Breadcrumb | null {
  if (breadcrumb.message) {
    breadcrumb.message = scrubString(breadcrumb.message) ?? breadcrumb.message;
  }
  if (breadcrumb.data) {
    breadcrumb.data = scrubObject(breadcrumb.data);
  }
  return breadcrumb;
}
